import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KoobDataTable, KoobInputCheckbox } from '@koob/ui';
import { formatDate } from '@koob/dates';
import { getFolderTravelerRow, getNationalityLabel } from '@koob/utils';
import FolderPassportExpiry from './FolderPassportExpiry';
import FolderTravelerTag from './FolderTravelerTag';

export interface FolderTravelersTableTraveler {
  id?: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  ageIsExact?: boolean;
  gender?: string;
  nationality?: string;
  passportNumber?: string;
  passportExpirationDate?: string;
}

export interface FolderTravelersTableProps {
  travelers: FolderTravelersTableTraveler[];
  /** Trip departure and return: Adult / Child and the passport warning. */
  departureDate?: string | null;
  returnDate?: string | null;
  /** Archived folder: no edit, no add. */
  readOnly?: boolean;
  onEdit: (traveler: FolderTravelersTableTraveler) => void;
  onAdd: () => void;
}

const GENDER_LABEL_KEY: Record<string, string> = {
  male: 'misc:genders.male',
  female: 'misc:genders.female',
  non_binary: 'misc:genders.other',
};

const TextAction = ({
  children,
  onClick,
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  testId: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="cursor-pointer border-none bg-transparent p-0 text-xs font-semibold text-brand hover:underline"
  >
    {children}
  </button>
);

/**
 * KD-3842 travelers section of a client folder: collapsible card with the
 * travelers table, Adult / Child and age at the departure date, passport
 * expiry warned against the return date.
 */
export default function FolderTravelersTable({
  travelers,
  departureDate = null,
  returnDate = null,
  readOnly = false,
  onEdit,
  onAdd,
}: FolderTravelersTableProps) {
  const { t } = useTranslation('folders');
  const [open, setOpen] = useState(true);
  const [approximateOnly, setApproximateOnly] = useState(false);

  const rows = travelers
    .filter((traveler) => !approximateOnly || !traveler.ageIsExact)
    .map((traveler) =>
      getFolderTravelerRow(traveler, { departureDate, returnDate }),
    );

  const header = (
    <div
      className={`flex items-center gap-2.5 px-4 py-3 ${
        open ? 'border-0 border-b border-solid border-border' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid="toggle-folder-travelers"
        className="flex flex-1 cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 text-left"
      >
        <span className="text-sm font-bold text-gray-900">
          {t('travelers.title')}
        </span>
        <span className="inline-flex h-5 items-center rounded-full bg-gray-100 px-2 text-[11px] font-bold text-gray-500">
          {travelers.length}
        </span>
      </button>
      {travelers.length > 0 && (
        <KoobInputCheckbox
          size="sm"
          checked={approximateOnly}
          onCheckedChange={setApproximateOnly}
          label={
            <span className="text-xs font-normal text-gray-500">
              {t('approximateClients')}
            </span>
          }
          data-testid="is-approximative-birthdate"
        />
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t('travelers.title')}
        className="cursor-pointer border-none bg-transparent p-0"
      >
        <i
          className={`fa-solid fa-chevron-down text-xs text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
    </div>
  );

  if (!open) {
    return (
      <div className="w-full overflow-hidden rounded-md border border-solid border-border bg-card">
        {header}
      </div>
    );
  }

  const columns = [
    {
      key: 'name',
      label: t('travelers.columns.name'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{row.name || '--'}</span>
          <FolderTravelerTag isChild={row.isChild} />
        </span>
      ),
    },
    {
      key: 'birthdate',
      label: t('travelers.columns.birthdate'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) =>
        row.traveler.birthdate ? (
          <span className="flex flex-col">
            <span>{formatDate(row.traveler.birthdate, 'DD/MM/YYYY')}</span>
            {row.ageAtDeparture !== null && (
              <span className="text-xs text-gray-500">
                {t('travelers.age', { count: row.ageAtDeparture })}
              </span>
            )}
          </span>
        ) : (
          <span className="text-gray-400">--</span>
        ),
    },
    {
      key: 'gender',
      label: t('travelers.columns.gender'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) =>
        GENDER_LABEL_KEY[row.traveler.gender]
          ? t(GENDER_LABEL_KEY[row.traveler.gender])
          : '--',
    },
    {
      key: 'nationality',
      label: t('travelers.columns.nationality'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) =>
        getNationalityLabel(row.traveler.nationality) ?? '--',
    },
    {
      key: 'passport',
      label: t('travelers.columns.passport'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) =>
        row.traveler.passportNumber || '--',
    },
    {
      key: 'expiry',
      label: t('travelers.columns.expiry'),
      render: (row: ReturnType<typeof getFolderTravelerRow>) => (
        <FolderPassportExpiry
          expirationDate={row.traveler.passportExpirationDate}
          expiresBeforeReturn={row.expiresBeforeReturn}
          returnDate={returnDate}
        />
      ),
    },
  ];

  return (
    <KoobDataTable
      headerBar={header}
      columns={columns}
      data={rows}
      actions={
        readOnly
          ? undefined
          : (row: ReturnType<typeof getFolderTravelerRow>) => (
              <TextAction
                onClick={() => onEdit(row.traveler)}
                testId="folder-traveler-edit"
              >
                {t('misc:edit')}
              </TextAction>
            )
      }
      footer={
        readOnly ? null : (
          <TextAction onClick={onAdd} testId="folder-add-traveler">
            {t('travelers.add')}
          </TextAction>
        )
      }
    />
  );
}
